export const DEFAULT_SCHEDULE_TIMEZONE =
    "America/Monterrey";

function parseMatchDateParts(
    dateValue: string
): {
    year: number;
    month: number;
    day: number;
} | null {
    const value = String(dateValue || "").trim();

    const isoMatch = value.match(
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/
    );

    if (isoMatch) {
        return {
            year: Number(isoMatch[1]),
            month: Number(isoMatch[2]),
            day: Number(isoMatch[3])
        };
    }

    const latinMatch = value.match(
        /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/
    );

    if (latinMatch) {
        return {
            year: Number(latinMatch[3]),
            month: Number(latinMatch[2]),
            day: Number(latinMatch[1])
        };
    }

    return null;
}

function parseMatchHourParts(
    hourValue: string
): {
    hour: number;
    minute: number;
} | null {
    const value = String(hourValue || "")
        .trim()
        .toUpperCase();

    if (!value) {
        return null;
    }

    const twelveHourMatch = value.match(
        /^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/
    );

    if (twelveHourMatch) {
        let hour = Number(twelveHourMatch[1]);
        const minute = Number(
            twelveHourMatch[2] || "0"
        );

        const period = twelveHourMatch[3];

        if (
            !Number.isFinite(hour) ||
            !Number.isFinite(minute) ||
            hour < 1 ||
            hour > 12 ||
            minute < 0 ||
            minute > 59
        ) {
            return null;
        }

        if (period === "AM" && hour === 12) {
            hour = 0;
        }

        if (period === "PM" && hour !== 12) {
            hour += 12;
        }

        return {
            hour,
            minute
        };
    }

    const twentyFourHourMatch = value.match(
        /^(\d{1,2})(?::(\d{2}))?(?::\d{2})?$/
    );

    if (!twentyFourHourMatch) {
        return null;
    }

    const hour = Number(twentyFourHourMatch[1]);
    const minute = Number(
        twentyFourHourMatch[2] || "0"
    );

    if (
        !Number.isFinite(hour) ||
        !Number.isFinite(minute) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59
    ) {
        return null;
    }

    return {
        hour,
        minute
    };
}

function getTimeZoneOffsetMilliseconds(
    date: Date,
    timeZone: string
): number {
    const formatter = new Intl.DateTimeFormat(
        "en-US",
        {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hourCycle: "h23"
        }
    );

    const values: Record<string, number> = {};

    formatter.formatToParts(date).forEach(function (part) {
        if (part.type === "literal") {
            return;
        }

        values[part.type] = Number(part.value);
    });

    const zonedAsUtc = Date.UTC(
        values.year,
        (values.month || 1) - 1,
        values.day || 1,
        values.hour || 0,
        values.minute || 0,
        values.second || 0
    );

    return zonedAsUtc - date.getTime();
}

function createDateFromSourceTimeZone(
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
    sourceTimeZone: string
): Date {
    const wallClockAsUtc = Date.UTC(
        year,
        month - 1,
        day,
        hour,
        minute,
        0
    );

    let timestamp = wallClockAsUtc;

    for (
        let index = 0;
        index < 2;
        index += 1
    ) {
        const offset =
            getTimeZoneOffsetMilliseconds(
                new Date(timestamp),
                sourceTimeZone
            );

        timestamp = wallClockAsUtc - offset;
    }

    return new Date(timestamp);
}

export function getMatchStartDate(
    dateValue: string,
    hourValue: string,
    sourceTimeZone = DEFAULT_SCHEDULE_TIMEZONE
): Date | null {
    const dateParts = parseMatchDateParts(dateValue);
    const hourParts = parseMatchHourParts(hourValue);

    if (!dateParts || !hourParts) {
        return null;
    }

    // Evitar fechas inexistentes como 2026-02-30.
    const calendarDate = new Date(
        Date.UTC(
            dateParts.year,
            dateParts.month - 1,
            dateParts.day
        )
    );

    if (
        calendarDate.getUTCFullYear() !== dateParts.year ||
        calendarDate.getUTCMonth() !== dateParts.month - 1 ||
        calendarDate.getUTCDate() !== dateParts.day
    ) {
        return null;
    }

    const startDate = createDateFromSourceTimeZone(
        dateParts.year,
        dateParts.month,
        dateParts.day,
        hourParts.hour,
        hourParts.minute,
        sourceTimeZone
    );

    return Number.isNaN(startDate.getTime())
        ? null
        : startDate;
}

export function formatLocalMatchSchedule(
    dateValue: string,
    hourValue: string,
    sourceTimeZone = DEFAULT_SCHEDULE_TIMEZONE
): string {
    const dateParts = parseMatchDateParts(dateValue);
    const hourParts = parseMatchHourParts(hourValue);

    if (!dateParts || !hourParts) {
        return "";
    }

    const sourceDate = createDateFromSourceTimeZone(
        dateParts.year,
        dateParts.month,
        dateParts.day,
        hourParts.hour,
        hourParts.minute,
        sourceTimeZone
    );

    if (Number.isNaN(sourceDate.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat(
        "es-MX",
        {
            weekday: "short",
            hour: "numeric",
            minute: "2-digit",
            hour12: true
        }
    )
        .format(sourceDate)
        .replace(",", "");
}

export function formatLocalWaiverSchedule(
    value?: string
): string {
    const normalizedValue = String(value || "").trim();

    if (!normalizedValue) {
        return "";
    }

    const date = new Date(normalizedValue);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat(
        "es-MX",
        {
            weekday: "long",
            day: "numeric",
            month: "long",
            hour: "numeric",
            minute: "2-digit",
            hour12: true
        }
    ).format(date);
}
